import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import {
  KONFIGURATION_FEHLT_TYPE,
  konfigurationFehltProblem,
  type KonfigurationFehltProblem,
} from '@brandschutz/shared';

export interface ApiProblem {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: Array<{ field?: string; message: string }>;
  /** Erweiterung bei fehlender Betriebskonfiguration (siehe istKonfigurationFehlt). */
  konfiguration?: KonfigurationFehltProblem['konfiguration'];
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ApiProblem;

  constructor(problem: ApiProblem) {
    super(problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
  }
}

let accessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onUnauthorizedListener(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

export const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const { data } = await axios.post<{ accessToken: string }>(
        '/api/v1/auth/refresh',
        {},
        { withCredentials: true },
      );
      accessToken = data.accessToken;
      return data.accessToken;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    const status = error.response?.status;

    // Skip refresh for auth endpoints themselves
    const url = original?.url ?? '';
    const isAuthEndpoint =
      url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout');

    if (status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        const newToken = await refreshAccessToken();
        if (original.headers) {
          original.headers.Authorization = `Bearer ${newToken}`;
        }
        return api.request(original);
      } catch {
        accessToken = null;
        onUnauthorized?.();
      }
    }

    const data = error.response?.data as Partial<ApiProblem> | undefined;
    const problem: ApiProblem = {
      type: data?.type,
      title: data?.title ?? error.message ?? 'Unbekannter Fehler',
      status: status ?? 0,
      detail: data?.detail,
      instance: data?.instance,
      errors: data?.errors,
      konfiguration: data?.konfiguration,
    };
    return Promise.reject(new ApiError(problem));
  },
);

/**
 * Type-guard für ApiError
 */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/**
 * Fehlt eine Betriebskonfiguration (APP_BASE_URL, SMTP, KI-Anbieter), ist das
 * kein Absturz, sondern ein erklärbarer Zustand: Der Aufrufer zeigt dann
 * `<KonfigurationsHinweis>` statt eines Toasts mit rohem Problem-Text.
 *
 * Zod validiert die Antwort, weil sie von außen kommt — was die Form nicht
 * erfüllt, ist für uns kein Konfigurationsfall und läuft in den normalen
 * Fehlerweg.
 */
export function istKonfigurationFehlt(err: unknown): KonfigurationFehltProblem | null {
  if (!isApiError(err)) return null;
  if (err.problem.type !== KONFIGURATION_FEHLT_TYPE) return null;
  const parsed = konfigurationFehltProblem.safeParse(err.problem);
  return parsed.success ? parsed.data : null;
}

/**
 * Bearer-Auth-Header für direkte `fetch()`-Aufrufe (z. B. ArrayBuffer-Downloads).
 * Liefert leeres Objekt, wenn kein Token gesetzt ist.
 */
export function authHeader(): { Authorization?: string } {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}
